
import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";
import { MedewerkerResponse, MedewerkerInsert, MedewerkerUpdate } from "@/integrations/supabase/medewerkerTypes";
import { useMedewerkerQueries } from "@/hooks/medewerkers/useMedewerkerQueries";
import MedewerkersHeader from "@/components/medewerkers/MedewerkersHeader";
import MedewerkersContent from "@/components/medewerkers/MedewerkersContent";
import MedewerkerFormDialog from "@/components/medewerkers/MedewerkerFormDialog";
import DeleteMedewerkerDialog from "@/components/medewerkers/DeleteMedewerkerDialog";
import DetailPageTemplate from "@/components/DetailPageTemplate";
import { toast } from "sonner";

const Medewerkers = () => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedMedewerker, setSelectedMedewerker] = useState<MedewerkerResponse | null>(null);
  
  const { 
    medewerkers, 
    isLoading, 
    addMutation, 
    updateMutation, 
    deleteMutation,
    getAssignedPackers
  } = useMedewerkerQueries();

  // Check permissions
  useEffect(() => {
    console.log("Medewerkers page - checking permissions");
    console.log("Read permission:", hasPermission("medewerkers", "read"));
    console.log("Write permission:", hasPermission("medewerkers", "write"));
    console.log("Delete permission:", hasPermission("medewerkers", "delete"));
  }, [hasPermission]);

  const handleOpenForm = async (medewerker?: MedewerkerResponse) => {
    if (!hasPermission("medewerkers", "write")) {
      toast.error("Je hebt geen rechten om medewerkers te bewerken");
      return;
    }
    
    setSelectedMedewerker(medewerker || null);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedMedewerker(null);
  };

  const handleDelete = (id: string) => {
    if (!hasPermission("medewerkers", "delete")) {
      toast.error("Je hebt geen rechten om medewerkers te verwijderen");
      return;
    }
    const medewerker = medewerkers.find(m => m.id === id);
    if (medewerker) {
      setSelectedMedewerker(medewerker);
      setIsDeleteDialogOpen(true);
    }
  };

  const handleSubmit = (data: MedewerkerInsert | MedewerkerUpdate) => {
    if (!hasPermission("medewerkers", "write")) {
      toast.error("Je hebt geen rechten om medewerkers te bewerken");
      return;
    }
    
    if (selectedMedewerker) {
      updateMutation.mutate({ id: selectedMedewerker.id, data });
    } else {
      addMutation.mutate(data as MedewerkerInsert);
    }
  };

  const handleConfirmDelete = (id: string) => {
    if (!hasPermission("medewerkers", "delete")) {
      toast.error("Je hebt geen rechten om medewerkers te verwijderen");
      return;
    }
    deleteMutation.mutate(id);
  };

  return (
    <DetailPageTemplate
      title="Medewerkers"
      backButtonProps={{
        label: "Terug naar dashboard",
        onClick: () => window.history.back()
      }}
    >
      <div className="space-y-6">
        <MedewerkersHeader 
          onAddMedewerker={() => handleOpenForm()} 
          canAdd={hasPermission("medewerkers", "write")}
        />
        
        <MedewerkersContent 
          isLoading={isLoading} 
          medewerkers={medewerkers} 
          onEdit={handleOpenForm} 
          onDelete={handleDelete}
          canEdit={hasPermission("medewerkers", "write")}
          canDelete={hasPermission("medewerkers", "delete")} 
        />

        <MedewerkerFormDialog 
          isOpen={isFormOpen}
          onOpenChange={setIsFormOpen}
          selectedMedewerker={selectedMedewerker}
          onSubmit={handleSubmit}
          onCancel={handleCloseForm}
          isSubmitting={addMutation.isPending || updateMutation.isPending}
        />

        <DeleteMedewerkerDialog 
          isOpen={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          medewerker={selectedMedewerker}
          onDelete={handleConfirmDelete}
          isDeleting={deleteMutation.isPending}
        />
      </div>
    </DetailPageTemplate>
  );
};

export default Medewerkers;
